import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getPricingPath, getSettingsPath } from "@/utils/localePaths";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { User, Settings, LogOut, Building2, Mail, MapPin, ArrowLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

function AboutPage() {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { user, logout } = useAuth();

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': 'Free',
      'pro_monthly': 'Pro Monthly',
      'business_monthly': 'Business Monthly',
      'persona_staff': 'Staff'
    };
    return planNames[planType] || planType;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            >
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {user && (
              <div className="flex items-center gap-2">
                <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white">Credits: </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                    Upgrade
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getSettingsPath(locale)} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Account Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {!user && (
              <div className="flex items-center gap-2">
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold w-40 py-1 text-sm">
                    Pricing
                  </Button>
                </Link>
                <Link to={getLoginPath(locale)}>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm">
                    SIGN IN
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 px-4 py-4">
        <div className="max-w-[720px] mx-auto">

          {/* Hero - Back Button + Logo (Compact) */}
          <div className="flex items-start gap-4 mb-6">
            {/* Back Button - Left aligned */}
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-[#FFDD59] transition-colors text-sm mt-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            
            {/* Logo - Centered */}
            <div className="flex-1 text-center">
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA" 
                className="h-10 w-auto mx-auto"
              />
            </div>
            
            {/* Spacer for balance */}
            <div className="w-[52px]"></div>
          </div>

          {/* Corporate Identity - Modern Card */}
          <div className="mb-6 bg-gray-900/40 rounded-lg px-4 py-3 border border-gray-800/60">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-widest mb-2">
              Corporate Identity
            </p>
            <div className="space-y-1.5 text-sm">
              <p className="flex items-center gap-2 text-gray-500">
                <Building2 className="w-3 h-3 flex-shrink-0" />
                <span>Owned by <span className="text-gray-300 font-medium">Persona Event And Marketing Solutions FZ LLE</span></span>
              </p>
              <p className="flex items-center gap-2 text-gray-500">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span>Twin Towers, M1 Floor, Creative City, Fujairah Media Free Zone, Fujairah, UAE</span>
              </p>
              <p className="flex items-center gap-2 text-gray-500">
                <Mail className="w-3 h-3 flex-shrink-0" />
                <a href="mailto:support@opara.co" className="text-[#FFDD59] hover:underline">support@opara.co</a>
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-5">
            
            {/* Hero Section */}
            <section className="border-l-2 border-[#FFDD59] pl-4">
              <h2 className="text-base font-semibold text-[#FFDD59] mb-2">
                Have a sneak peek at your next paid partnership's return before you invest!
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                In the fast-paced world of influencer marketing, most decisions are still based on gut feelings. Brand managers often ask: <em className="text-gray-300">"Is this influencer worth the investment?"</em> and <em className="text-gray-300">"What is the realistic reach we can expect?"</em>
              </p>
              <p className="text-sm text-gray-500 italic mb-2">
                The Science of Influencer ROI on Instagram
              </p>
              <p className="text-sm text-gray-400 leading-relaxed">
                At <span className="text-white font-medium">OPARA</span>, we transform those questions into data-driven answers. We believe influencer marketing should be as measurable and predictable as any other performance channel.
              </p>
            </section>

            {/* Born from the Agency Trenches */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                Born from the Agency Trenches
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                OPARA isn't just another software tool. It was forged by <span className="text-gray-300">Persona</span>, an influencer marketing agency with in-depth experience in complex campaigns. We built it to solve real-world problems:
              </p>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">True Market Value:</span> Determining exactly what a partnership is worth based on performance, not just followers.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Predictive Performance:</span> Foreseeing results before a single dollar is spent.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Concrete Accountability:</span> Providing a data-backed foundation to measure success after the campaign.</span>
                </li>
              </ul>
            </section>

            {/* Technology Section */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                The Technology: Fail-Safe Data Intelligence
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                While most platforms struggle with social media data blocks—leading to "Data Not Found" errors—OPARA operates on a sophisticated, <span className="text-gray-300">Multi-Layered Data Harvesting</span> architecture.
              </p>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Autonomous Discovery:</span> We instantly analyze real-time profile activity patterns and content performance with just a username.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">AI-Driven Referee:</span> Our internal AI-based code analyzes content, captions, and brand mentions to filter out noise and focus on real commercial impact.</span>
                </li>
              </ul>
            </section>

            {/* Mission Section */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                Our Mission: Protecting Your Marketing Budget
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                Whether you are a startup or a global enterprise, our goal is to eliminate inefficiency. By using OPARA, you gain:
              </p>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Negotiation Power:</span> Support your offers with objective, third-party performance data.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Realistic Projections:</span> Know your expected Cost Per View (CPV) and engagement targets upfront.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Budget Optimization:</span> Measure the estimated return on every dollar spent to ensure your marketing capital is working its hardest.</span>
                </li>
              </ul>
            </section>

            {/* CTA - Minimal */}
            <div className="text-center py-6 border-t border-gray-800/40">
              <p className="text-sm text-gray-500 mb-3">
                Ready to make smarter influencer marketing decisions?
              </p>
              <Link to={getLoginPath(locale)}>
                <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold px-6 py-2 text-sm">
                  Get Started Free
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default AboutPage;
